import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';

import { NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import { Card } from '../Card/Card';
import { BackgroundCheckIcon } from '../BackgroundCheckIcon/BackgroundCheckIcon';
import {
  CertificatesTag,
  LanguaguesTag,
  LocationTag,
  TagComponent,
  YearsOfExperienceTag,
} from '../Tag/TagComponent';
import Icon from '../Icon/Icon';
import { truncate } from 'lodash';
import { OrganizationBadge } from '../OrganizationBadge/OrganizationBadge';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 500 });

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = props?.config || useConfiguration();
  const { listing = true } = props;
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', publicData } = currentListing.attributes;
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const slug = createSlug(title);

  const isMoreThenOneService = (publicData?.services?.length || 0) > 1;
  const enumOptions = config?.listing?.listingFields?.find(conf => conf.key === 'services')
    ?.enumOptions;

  const UrlOrNamedLink = ({ className, name, params, children }) => {
    if (props?.urlToListing) {
      return (
        <div className={className} onClick={() => (window.location = props?.urlToListing)}>
          {children}
        </div>
      );
    }
    return (
      <NamedLink className={className} name={name} params={params}>
        {children}
      </NamedLink>
    );
  };

  const FormattedMessageLocal = ({ id, defaultMessage, values }) => {
    if (props?.intl) {
      return values
        ? values?.authorName
        : props?.intl.formatMessage({
            id,
            defaultMessage,
          });
    }
    return <FormattedMessage id={id} defaultMessage={defaultMessage} values={values} />;
  };

  const isBackgroundCheck = currentListing?.attributes?.publicData?.background_check === 'yes';

  const isScaledMediumAvaiable = !!Object.keys(
    author?.profileImage?.attributes?.variants || {}
  )?.includes('scaled-medium');

  const variantsScaledMedium = ['scaled-medium'];
  const variantsSquareSmall = ['square-small2x'];
  const isOrganization =
    publicData?.i_am_agency === 'yes' && publicData?.organization_name?.length > 0;

  const priceNotProvided =
    (!!publicData?.price_min || !!publicData?.price_max) &&
    (typeof publicData?.price_min === 'number' || typeof publicData?.price_max === 'number');

  return (
    <UrlOrNamedLink className={css.root} name="ListingPage" params={{ id, slug }}>
      <Card className={css.rootInner}>
        <div className={css.topBarWrapper}>
          <div
            style={{
              pointerEvents: 'none',
            }}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={author?.profileImage}
              variants={isScaledMediumAvaiable ? variantsScaledMedium : variantsSquareSmall}
            />
          </div>
          <div className={css.header}>
            <div className={css.headerText}>
              <FormattedMessageLocal
                id="ListingCard.author"
                values={{
                  authorName: truncate(
                    isOrganization ? publicData?.organization_name : authorName,
                    { length: 22 }
                  ),
                }}
              />
              {isOrganization && <OrganizationBadge />}
              {isBackgroundCheck && <BackgroundCheckIcon />}
            </div>
            <div className={css.servicesWrapper}>
              {isMoreThenOneService ? (
                <>
                  {/* Show first service and button wit "See {amount} more" */}
                  {publicData?.services?.slice(0, 1).map((service, index) => {
                    const label = enumOptions?.find(option => option.option === service)?.label;
                    return <TagComponent key={index} text={label} />;
                  })}
                  <UrlOrNamedLink className={css.seeMore} name="ListingPage" params={{ id, slug }}>
                    <FormattedMessageLocal
                      id="ListingCard.seeMore"
                      values={{ amount: publicData?.services?.length - 1 }}
                      defaultMessage={'See {amount} more'}
                    />
                  </UrlOrNamedLink>
                </>
              ) : (
                <>
                  {publicData?.services?.map((service, index) => {
                    const label = enumOptions?.find(option => option.option === service)?.label;
                    return <TagComponent key={index} text={label} />;
                  })}
                </>
              )}
            </div>
          </div>
        </div>
        <div className={css.middleBarWrapper}>
          {publicData?.years_of_experience > 0 && (
            <YearsOfExperienceTag years={publicData?.years_of_experience} />
          )}
          <LocationTag text={publicData?.location?.address} />
          <LanguaguesTag languagues={publicData?.languages} />
          <CertificatesTag certificates={currentListing.images?.length || 0} />
        </div>
        <div className={css.bottomBarWrapper}>
          <div className={css.bottomBarWrapperLeft}>
            <div className={css.priceRange}>
              {priceNotProvided
                ? '$' + (publicData?.price_min || 0) + '-' + (publicData?.price_max || 0)
                : 'Price not provided'}
            </div>
            {priceNotProvided && (
              <div className={css.priceRangeLabel}>
                <FormattedMessageLocal
                  id="ListingCard.priceRangeLabel"
                  defaultMessage={'Price range'}
                />
              </div>
            )}
          </div>
          <div>
            <Icon icon={'QuestionMarkCircled'} />
          </div>
        </div>
      </Card>
    </UrlOrNamedLink>
  );
};

export default ListingCard;
